import { useCallback, useEffect, useRef, useState } from 'react'
import { IconVolume, IconVolumeMute } from '../ui/icons'

const HIDE_DELAY = 3000

/**
 * The brand hero video shown below the login page's "how it works" steps (mirrors v1's
 * FrontPageVideo). Autoplays muted + looped (the only way browsers allow unattended playback); a
 * mute toggle fades in on interaction so the customer can turn sound on. URL comes from
 * `tradeInConfig.video`; the caller renders this only when that's set.
 */
export function FrontPageVideo({ url }: { url: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>()
  const [muted, setMuted] = useState(true)
  const [buttonVisible, setButtonVisible] = useState(false)

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setButtonVisible(false), HIDE_DELAY)
  }, [])

  const showButton = useCallback(() => {
    setButtonVisible(true)
    scheduleHide()
  }, [scheduleHide])

  // Reveal the toggle briefly once the video scrolls into view, so it's discoverable.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          showButton()
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [showButton])

  useEffect(() => () => clearTimeout(hideTimer.current), [])

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    const v = videoRef.current
    if (v) {
      v.muted = !v.muted
      setMuted(v.muted)
    }
    scheduleHide()
  }

  return (
    <div
      ref={containerRef}
      className="fp-video"
      onMouseEnter={() => setButtonVisible(true)}
      onMouseLeave={() => setButtonVisible(false)}
      onClick={showButton}
    >
      <video ref={videoRef} playsInline autoPlay muted loop preload="metadata">
        <source src={url} type="video/mp4" />
      </video>
      <button
        type="button"
        className={`fp-video-mute${buttonVisible ? ' is-visible' : ''}`}
        onClick={toggleMute}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
      >
        {muted ? <IconVolumeMute /> : <IconVolume />}
      </button>
    </div>
  )
}
